import _ from 'lodash'
import { PropsWithChildren, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CellContext, createColumnHelper, RowData } from '@tanstack/react-table'

import { Chip, ChipProps, Paper, Stack, Tooltip } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import LaunchIcon from '@mui/icons-material/Launch'

import Table from '@/components/ui/tables/Table'
import ChipButton from '@/components/ui/buttons/ChipButton'

import { OrderStatus } from '@/core/models/order.model'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    acceptOrder?: (orderId: string) => void
  }
}

type ConsultationOrderData = {
  id: string
  acceptedBy?: string
  canAccept?: boolean
  createdBy: string
  consultationId: string
  isAccepting?: boolean
  patient: string
  speciality?: string
  status: OrderStatus
  startDate: string
}

type ConsultationOrdersProps = {
  consultationOrders: ConsultationOrderData[]
  onAccept?: (consultationOrderId: string) => void
  readOnly?: boolean
}

const columnHelper = createColumnHelper<ConsultationOrderData>()

export default function ConsultationOrders(props: ConsultationOrdersProps) {
  const { consultationOrders, onAccept, readOnly = false } = props
  const { t } = useTranslation()

  const columns = useMemo(
    () =>
      _.compact([
        columnHelper.accessor('createdBy', {
          header: t('table.consultationOrders.header.createdBy').toString(),
          cell: CellDecorator,
        }),
        columnHelper.accessor('acceptedBy', {
          header: t('table.consultationOrders.header.acceptedBy').toString(),
        }),
        columnHelper.accessor('patient', {
          header: t('table.consultationOrders.header.patient').toString(),
          cell: CellDecorator,
        }),
        columnHelper.accessor('status', {
          header: t('table.consultationOrders.header.status').toString(),
          cell: ConsultationOrderStatusCell,
        }),
        columnHelper.accessor('startDate', {
          header: t('table.consultationOrders.header.startDate').toString(),
          cell: CellDecorator,
        }),
        !readOnly &&
          columnHelper.display({
            id: 'actions',
            header: t('common:table.header.action'),
            cell: ActionsCell,
            meta: {
              align: 'right',
            },
          }),
      ]),
    [readOnly, t]
  )

  return (
    <Table
      key="network-consultation-orders"
      container={TableContainer}
      columns={columns}
      data={consultationOrders}
      infiniteScroll
      manualPagination
      meta={{
        acceptOrder: onAccept,
      }}
    />
  )
}

function TableContainer({ children }: PropsWithChildren<{}>) {
  const isMobile = useIsMobile()
  return <Paper sx={{ minHeight: isMobile ? '100px' : '600px' }}>{children}</Paper>
}

function ActionsCell(props: CellContext<ConsultationOrderData, unknown>) {
  const { row, table } = props
  const { t } = useTranslation()

  const { canAccept, consultationId, id, isAccepting, status } = row.original

  return (
    <Stack direction="row" alignItems="center" justifyContent="end" spacing={1}>
      {canAccept && status === OrderStatus.Pending && (
        <Tooltip title={t('common:button.accept.tooltip')}>
          <ChipButton
            loading={isAccepting}
            color="success"
            onClick={() => {
              table.options.meta?.acceptOrder?.(id)
            }}
          >
            <CheckRoundedIcon />
          </ChipButton>
        </Tooltip>
      )}
      <Tooltip title={t('table.consultationOrders.actions.openConsultation.tooltip')}>
        <ChipButton color="info" component={Link} to={`/consultations/${consultationId}`}>
          <LaunchIcon />
        </ChipButton>
      </Tooltip>
    </Stack>
  )
}

function CellDecorator({ getValue }: CellContext<ConsultationOrderData, string>) {
  return (
    <Stack alignItems="start" justifyContent="center" sx={{ minHeight: '30px' }}>
      {getValue()}
    </Stack>
  )
}

function ConsultationOrderStatusCell({ getValue }: CellContext<ConsultationOrderData, OrderStatus>) {
  let color: ChipProps['color']
  const status = getValue()
  switch (status) {
    case OrderStatus.Consultation_Created:
    case OrderStatus.Pending:
      color = 'primary'
      break
    case OrderStatus.Accepted:
      color = 'success'
      break
    case OrderStatus.Canceled:
    case OrderStatus.Refused:
      color = 'error'
      break
    default:
      color = 'info'
      break
  }
  return <Chip color={color} label={status} size="small" variant="filled" />
}
